import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';
import { Center, Stack, Text, Button } from '@mantine/core';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /** この値が変わるとエラー状態がリセットされる（例: location.pathname） */
  resetKey?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React.lazy の読み込み失敗時にエラー表示とリロード手段を提供する ErrorBoundary
 * resetKey（例: pathname）が変化するとエラー状態を自動リセットし、別ルートへの遷移を可能にする
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Center style={{ height: '100vh' }}>
          <Stack align="center" gap="md">
            <Text size="lg" fw={600}>
              ページの読み込みに失敗しました
            </Text>
            <Text size="sm" c="dimmed">
              {this.state.error?.message}
            </Text>
            <Button onClick={() => window.location.reload()}>ページをリロード</Button>
          </Stack>
        </Center>
      );
    }

    return this.props.children;
  }
}
